'use client'

import { useMemo } from 'react'

import { ArrowBtn } from '@/app/components/atoms/ArrowBtn'
import { TrackedProductCard } from '@/app/components/shared/TrackedProductCard'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { withIndex } from '@/app/lib/analytics/mapProductToAnalyticsItem'
import { useProductListTracking } from '@/app/lib/analytics/useProductListTracking'
import type { ProductCard } from '@/app/lib/api/mapProductToCard'
import { useCarouselProgress } from '@/app/lib/hooks/useCarouselProgress'
import { useDragScroll } from '@/app/lib/hooks/useDragScroll'
import { useRailScroll } from '@/app/lib/hooks/useRailScroll'
import { cx } from '@/app/lib/utils'

import { RankingCard } from './RankingCard'
import styles from './RankingCarousel.module.scss'
import { buildAnalyticsItemListId, buildAnalyticsModulePosition } from './ProductosLocalizados.constants'
import type { RankingCarouselAnalytics } from './ProductosLocalizados.types'

interface RankingCarouselProps {
  readonly items: readonly ProductCard[]
  /** Ver `RankingCarouselAnalytics` (`ProductosLocalizados.types.ts`). */
  readonly analytics?: RankingCarouselAnalytics
}

/**
 * Config inerte para `useProductListTracking` cuando no llega `analytics`
 * (p. ej. una página que aún no envuelve este módulo en
 * `PageAnalyticsProvider`) — nunca se llega a usar de verdad: sin `analytics`,
 * ninguna card se envuelve en `TrackedProductCard` más abajo, así que
 * `trackImpression`/`trackSelect` nunca se invocan. Solo existe para poder
 * llamar al hook de forma incondicional (reglas de Hooks de React).
 */
const NO_TRACKING: RankingCarouselAnalytics = {
  itemListId: '',
  itemListName: '',
  moduleName: '',
  moduleType: '',
  modulePosition: '',
  items: [],
}

/**
 * Carril de ranking del módulo CMS «ProductosLocalizados»: carrusel
 * horizontal en cualquier tamaño de pantalla, con flechas de avanzar/
 * retroceder superpuestas SOLO desde escritorio (`useRailScroll`, ya
 * compartido) y una barra de progreso continua en cualquier tamaño
 * (`useCarouselProgress`, promovido a `src/app/lib/hooks/` — ver su doc).
 * En móvil/tablet el
 * desplazamiento es solo por gesto táctil, sin flechas.
 *
 * El número de puesto (`rank`) se deriva del índice dentro de `items`
 * (1-based): `items` ya llega recortado a los 10 primeros utilizables por
 * `ProductosLocalizados.tsx` (FR-017), así que el puesto 1 siempre es el
 * primer elemento de este array.
 *
 * `'use client'` por las flechas y por el cálculo de progreso: el resto del
 * módulo (`ProductosLocalizados.tsx`) es Server Component.
 */
export function RankingCarousel({ items, analytics }: RankingCarouselProps) {
  const { railRef, atStart, atEnd, recalc: recalcArrows, scrollByStep } = useRailScroll(items.length)
  const { hasOverflow, thumbWidth, thumbLeft, recalc: recalcProgress } = useCarouselProgress(
    railRef,
    items.length,
  )
  // Con ratón el carril no se arrastra de forma nativa (solo el gesto táctil).
  useDragScroll(railRef, items.length)
  const pageAnalytics = useOptionalPageAnalytics()
  // `item_list_id`/`module_position` dependen de la página anfitriona: el
  // server component los calcula con el valor de Home por defecto y aquí se
  // ajustan al `page_type` real del provider (Home queda idéntica).
  const pageType = pageAnalytics?.context.page_type
  const resolvedAnalytics = useMemo<RankingCarouselAnalytics>(() => {
    if (!analytics) return NO_TRACKING
    if (!pageType || analytics.location === undefined) return analytics
    return {
      ...analytics,
      itemListId: buildAnalyticsItemListId(analytics.location, pageType),
      modulePosition: buildAnalyticsModulePosition(pageType),
    }
  }, [analytics, pageType])
  const { trackImpression, trackSelect } = useProductListTracking(resolvedAnalytics, {
    trackViewItemList: pageAnalytics?.trackViewItemList,
    trackSelectItem: pageAnalytics?.trackSelectItem,
  })

  const handleScroll = (): void => {
    recalcArrows()
    recalcProgress()
  }

  return (
    <div className={styles.container}>
      <div className={styles.viewport}>
        <div
          ref={railRef}
          className={styles.rail}
          data-fade-end={!atEnd}
          onScroll={handleScroll}
          data-testid="ranking-carousel"
        >
          {items.map(({ id, ...card }, index) => {
            const rankingCard = <RankingCard rank={index + 1} {...card} />
            const item = analytics?.items[index]
            if (!item) return <div key={id} className={styles.item}>{rankingCard}</div>

            return (
              <div key={id} className={styles.item}>
                <TrackedProductCard
                  index={index}
                  onImpression={() => trackImpression(item.item_id, withIndex(item, index))}
                  onSelect={() => trackSelect(withIndex(item, index))}
                >
                  {rankingCard}
                </TrackedProductCard>
              </div>
            )
          })}
        </div>

        <div className={styles.nav}>
          {!atStart && (
            <ArrowBtn
              dir="left"
              overlay
              className={cx(styles.navBtn, styles.navBtnLeft)}
              onClick={() => scrollByStep(-1)}
            />
          )}
          {!atEnd && (
            <ArrowBtn
              dir="right"
              overlay
              className={cx(styles.navBtn, styles.navBtnRight)}
              onClick={() => scrollByStep(1)}
            />
          )}
        </div>
      </div>

      {hasOverflow && (
        <div
          className={styles.progress}
          role="progressbar"
          aria-label="Progreso del ranking de productos"
          aria-valuenow={Math.round(thumbLeft)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className={styles.track}>
            <div
              className={styles.thumb}
              style={{ width: `${thumbWidth}%`, left: `${thumbLeft}%` }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
